import React, { useState, useEffect } from 'react';
import { TrustedAdvisorService } from '../../lib/trustedAdvisorService';
import { supabase } from '../../lib/supabase';
import { 
  TrustedAdvisor, 
  AdvisorPermissionsSummary,
  TrustedAdvisorFilters,
  AdvisorStatus 
} from '../../types/trustedAdvisor';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Select } from '../common/Select';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { 
  Users, 
  Search, 
  Filter, 
  MoreVertical, 
  Mail, 
  Phone,
  Edit,
  Trash2,
  RefreshCw,
  Shield,
  CheckCircle,
  Clock,
  X,
  User
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import TrustedAdvisorCard from './TrustedAdvisorCard';
import TrustedAdvisorEditModal from './TrustedAdvisorEditModal';

interface TrustedAdvisorListProps {
  onRefresh: () => void;
  onAddAdvisor: () => void;
}

const TrustedAdvisorList: React.FC<TrustedAdvisorListProps> = ({ onRefresh, onAddAdvisor }) => {
  const [advisors, setAdvisors] = useState<TrustedAdvisor[]>([]);
  const [permissionsSummary, setPermissionsSummary] = useState<AdvisorPermissionsSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TrustedAdvisorFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAdvisor, setSelectedAdvisor] = useState<TrustedAdvisor | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);

  useEffect(() => {
    loadAdvisors();
    loadPendingInvitations();
  }, [filters]);

  const loadPendingInvitations = async () => {
    try {
      setLoadingInvitations(true);
      const { data, error } = await supabase
        .from('trusted_advisor_invitations')
        .select('*')
        .eq('inviter_id', user?.id)
        .eq('status', 'pending')
        .order('sent_at', { ascending: false });

      if (error) throw error;
      setPendingInvitations(data || []);
    } catch (err) {
      console.error('Failed to load pending invitations:', err);
    } finally {
      setLoadingInvitations(false);
    }
  };

  const loadAdvisors = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [advisorsData, permissionsData] = await Promise.all([
        TrustedAdvisorService.getTrustedAdvisors(filters),
        TrustedAdvisorService.getAdvisorsWithPermissions()
      ]);
      
      setAdvisors(advisorsData);
      setPermissionsSummary(permissionsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trusted advisors');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleStatusFilter = (status: AdvisorStatus | 'all') => {
    setFilters(prev => ({ 
      ...prev, 
      status: status === 'all' ? undefined : status 
    }));
  };

  const handleRelationshipFilter = (relationship: string) => {
    setFilters(prev => ({ 
      ...prev, 
      relationship: relationship === 'all' ? undefined : relationship 
    }));
  };

  const handleEditAdvisor = (advisor: TrustedAdvisor) => {
    setSelectedAdvisor(advisor);
    setShowEditModal(true);
  };

  const handleRemoveAdvisor = async (advisorId: string) => {
    if (!confirm('Are you sure you want to remove this trusted advisor? This action cannot be undone.')) {
      return;
    }

    try {
      await TrustedAdvisorService.removeTrustedAdvisor(advisorId);
      await loadAdvisors();
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove trusted advisor');
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      // Update existing invitation with new token and expiry
      const { data: tokenData } = await supabase.rpc('generate_secure_invitation_token');
      const newExpiry = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

      await supabase
        .from('trusted_advisor_invitations')
        .update({
          invitation_token: tokenData,
          expires_at: newExpiry,
          sent_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', invitationId);

      // TODO: Send new invitation email
      // await SendGridService.sendTrustedAdvisorInvitation({...});
      
      await loadPendingInvitations();
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend invitation');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await supabase
        .from('trusted_advisor_invitations')
        .update({
          status: 'expired',
          updated_at: new Date().toISOString()
        })
        .eq('id', invitationId);

      await loadPendingInvitations();
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel invitation');
    }
  };

  const handleManagePermissions = (advisorId: string) => {
    // This would navigate to the permissions tab with the advisor pre-selected
    console.log('Navigate to permissions for advisor:', advisorId);
  };

  const getStatusColor = (status: AdvisorStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'removed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: AdvisorStatus) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'removed':
        return <X className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  const getPermissionsForAdvisor = (advisorId: string) => {
    return permissionsSummary.find(p => p.advisor_id === advisorId);
  };

  const relationships = Array.from(new Set(advisors.map(a => a.relationship)));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Trusted Advisors</h2>
          <p className="text-gray-600 mt-1">
            Manage your trusted advisors and their access permissions
          </p>
        </div>
        <Button onClick={loadAdvisors} variant="outline" className="flex items-center">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search advisors..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <Select
              value={filters.status || 'all'}
              onChange={(value) => handleStatusFilter(value as AdvisorStatus | 'all')}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'active', label: 'Active' },
                { value: 'pending', label: 'Pending' },
                { value: 'removed', label: 'Removed' }
              ]}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Relationship
            </label>
            <Select
              value={filters.relationship || 'all'}
              onChange={(value) => handleRelationshipFilter(value)}
              options={[
                { value: 'all', label: 'All Relationships' },
                ...relationships.map(r => ({ value: r, label: r }))
              ]}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Has Permissions
            </label>
            <Select
              value={filters.has_permissions === undefined ? 'all' : filters.has_permissions ? 'yes' : 'no'}
              onChange={(value) => setFilters(prev => ({ 
                ...prev, 
                has_permissions: value === 'all' ? undefined : value === 'yes' 
              }))}
              options={[
                { value: 'all', label: 'All Advisors' },
                { value: 'yes', label: 'With Permissions' },
                { value: 'no', label: 'Without Permissions' }
              ]}
            />
          </div>
        </div>
      </Card>

      {/* Error Display */}
      {error && (
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center">
            <X className="w-5 h-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
            <Button
              onClick={() => setError(null)}
              variant="ghost"
              size="sm"
              className="ml-auto"
            >
              Dismiss
            </Button>
          </div>
        </Card>
      )}

      {/* Pending Invitations Section */}
      {pendingInvitations.length > 0 && (
        <Card className="p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Pending Invitations</h3>
            <Badge variant="default">{pendingInvitations.length}</Badge>
          </div>
          <div className="space-y-3">
            {pendingInvitations.map((invitation) => (
              <div key={invitation.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {invitation.invited_first_name} {invitation.invited_last_name}
                    </p>
                    <p className="text-sm text-gray-600">{invitation.invited_email}</p>
                    <p className="text-xs text-gray-500">{invitation.relationship}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={() => handleResendInvitation(invitation.id)}
                    variant="outline"
                    size="sm"
                    disabled={loadingInvitations}
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Resend
                  </Button>
                  <Button
                    onClick={() => handleCancelInvitation(invitation.id)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    disabled={loadingInvitations}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Advisors List */}
      <div className="space-y-4">
        {advisors.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No trusted advisors found
            </h3>
            <p className="text-gray-500 mb-4">
              {filters.status || filters.relationship || filters.search || filters.has_permissions !== undefined
                ? 'Try adjusting your filters to see more results.'
                : 'Get started by adding your first trusted advisor.'
              }
            </p>
            {!filters.status && !filters.relationship && !filters.search && filters.has_permissions === undefined && (
              <Button variant="primary" onClick={onAddAdvisor}>
                Add Trusted Advisor
              </Button>
            )}
          </Card>
        ) : (
          advisors.map((advisor) => {
            const permissions = getPermissionsForAdvisor(advisor.id);
            return (
              <TrustedAdvisorCard
                key={advisor.id}
                advisor={advisor}
                permissions_summary={permissions}
                onEdit={handleEditAdvisor}
                onRemove={handleRemoveAdvisor}
                onResendInvitation={handleResendInvitation}
                onManagePermissions={handleManagePermissions}
              />
            );
          })
        )}
      </div>

      {/* Edit Modal */}
      {selectedAdvisor && (
        <TrustedAdvisorEditModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedAdvisor(null);
          }}
          advisor={selectedAdvisor}
          onSave={async () => {
            await loadAdvisors();
            onRefresh();
            setShowEditModal(false);
            setSelectedAdvisor(null);
          }}
        />
      )}
    </div>
  );
};

export default TrustedAdvisorList; 